import { Box, IconButton } from '@mui/material';
import React, { useEffect, useRef } from 'react';

import { FlowProvider } from '../../contexts';
import {
  storylineContextManager,
  type StorylineContext,
} from '../../services/storylineContext';

import { ChatbotWidget } from './ChatbotWidget';

import './chatbot.css';

/** Path to close icon image */
const CLOSE_ICON_PATH = '/assets/images/installation-guide/close.png';

interface ChatbotModalProps {
  /** Flow ID to initialize the chatbot with */
  flowId?: string;
  /** Canonical guide context for backend prompt shaping */
  guideContext?: StorylineContext;
  /** Callback when the modal is closed */
  onClose: () => void;
}

/**
 * ChatbotModal - Full-screen modal wrapper for chatbot integration
 * 
 * Used when the chatbot is opened from the installation guide via the
 * modal route pattern. This ensures window.location.href is /chatbot
 * during chatbot interactions for Mixpanel analytics parity.
 * 
 * @example
 * ```tsx
 * // In App.tsx, render when background location exists
 * {background && location.pathname === '/chatbot' && (
 *   <ChatbotModal 
 *     flowId={location.state?.flowId}
 *     onClose={() => history.goBack()}
 *   />
 * )}
 * ```
 */
export const ChatbotModal: React.FC<ChatbotModalProps> = ({
  flowId,
  guideContext,
  onClose,
}) => {
  const previousContextRef = useRef<StorylineContext | null>(null);

  useEffect(() => {
    if (!guideContext || (!guideContext.mainStepId && !guideContext.helpFlowId)) {
      return undefined;
    }

    previousContextRef.current = storylineContextManager.getContext();
    storylineContextManager.clear();
    storylineContextManager.setContext(guideContext);

    return () => {
      const previousContext = previousContextRef.current;

      storylineContextManager.clear();

      if (previousContext && (previousContext.mainStepId || previousContext.helpFlowId)) {
        storylineContextManager.setContext(previousContext);
      }
    };
  }, [guideContext]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={__('Close chatbot')}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 1,
            width: 40,
            height: 40,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Box
            component="img"
            src={CLOSE_ICON_PATH}
            alt=""
            sx={{ width: 20, height: 20 }}
          />
        </IconButton>
      </Box>

      {/* Chatbot content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        className="chatbot-page"
      >
        <FlowProvider
          enableFallbackMode={true}
          enablePostMessage={false}
        >
          <ChatbotWidget
            flowId={flowId}
            enableFlowMode={true}
            onRequestClose={onClose}
          />
        </FlowProvider>
      </Box>
    </Box>
  );
};
